import {
  Block,
  RichTextBlock,
  blockTypesRecord,
} from '@basehub/data/replicache/index'
import { getNodeBreadcrumbs, transformRecentNodes } from './link-utils'
import { usePossibleReferences } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/reference/use-possible-references'
import { useRecentOpenedBlocks } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/components/recent-opened-tabs'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { LinkAttributes } from '@basehub/tiptap-api-utils'
import { TreeNode } from '@basehub/utils/tree/index'
import * as React from 'react'

export const NEW_LINK_OPTION = 'NEW_LINK_VALUE'

export type InsertLinkOption = {
  label: string
  value: string
  breadcrumbs?: string[]
  type?: Block['type']
  node?: Block
}

interface UseLinkOptionsProps {
  block: RichTextBlock
  search: string
  markAttributes: LinkAttributes
}
export const useLinkOptions = ({
  block,
  search,
  markAttributes,
}: UseLinkOptionsProps) => {
  const getNode = useWorkingTreeStore((s) => s.treeRef.current?.getNode)
  const { getPossibleReferences } = usePossibleReferences()
  const recentOpenedBlocks = useRecentOpenedBlocks()

  // Get all possible reference nodes for this block
  const possibleReferenceNodes = getPossibleReferences(block).filter(
    (b) => !b.value.hidden
  )

  // Get current node if we have an internal link and ALWAYS transform its ID
  const currentSettedNode =
    markAttributes.type === 'internal' && markAttributes.targetId
      ? getNode?.(markAttributes.targetId)
      : null

  // Create base option transformer - ALWAYS use idToSourceBlockId
  const createOption = React.useCallback(
    (node: TreeNode<Block>): InsertLinkOption => ({
      label: node.value.title || 'Untitled',
      value: idToSourceBlockId(node.value),
      breadcrumbs: getNode ? getNodeBreadcrumbs(node, getNode) : [],
      type: node.value.type,
      node: node.value,
    }),
    [getNode]
  )

  // Transform recent blocks - ensure IDs are transformed
  const recentOptions = React.useMemo(
    () =>
      transformRecentNodes({
        nodes: recentOpenedBlocks.recentBlocks,
        getNode,
        max: 5,
        filter: (block) => {
          if (block.hidden) return false
          const isValidType = [
            blockTypesRecord.instance,
            blockTypesRecord.component,
          ].includes(block.type)
          if (!isValidType) return false
          return possibleReferenceNodes.some(
            (node) => idToSourceBlockId(node.value) === idToSourceBlockId(block)
          )
        },
      }).map((option) => ({
        ...option,
        value: idToSourceBlockId(option.node!),
      })),
    [recentOpenedBlocks.recentBlocks, possibleReferenceNodes, getNode]
  )

  const currentOption = currentSettedNode
    ? createOption(currentSettedNode)
    : null

  const filteredOptions = React.useMemo(() => {
    if (search) {
      const searchLower = search.toLowerCase()
      const baseOptions = [
        ...(currentOption ? [currentOption] : []),
        ...possibleReferenceNodes
          .filter(
            (node) =>
              idToSourceBlockId(node.value) !== currentSettedNode?.value.id
          )
          .map(createOption),
      ]

      return [
        ...baseOptions.filter((option) =>
          option.label.toLowerCase().includes(searchLower)
        ),
        { label: search, value: NEW_LINK_OPTION },
      ]
    }

    if (recentOptions.length) {
      if (currentOption) {
        return [
          currentOption,
          ...recentOptions.filter(
            (option) => idToSourceBlockId(option.node!) !== currentOption.value
          ),
        ].slice(0, 4)
      }
      return recentOptions
    }

    return possibleReferenceNodes.slice(0, 5).map(createOption)
  }, [
    search,
    recentOptions,
    possibleReferenceNodes,
    createOption,
    currentOption,
    currentSettedNode?.value.id,
  ])

  return { filteredOptions }
}
